// src/components/provider/MessagesTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getMessagesForProvider,
  sendMessage,
  deleteMessage,
} from "../APIs/messageService";
import { getPatient } from "../APIs/patientService";

export default function MessagesTab({ providerId }) {
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [nameCache, setNameCache] = useState({}); // patientId -> "First Last"
  const [activePatientId, setActivePatientId] = useState(null); // which thread is open
  const [composer, setComposer] = useState(""); // compose inside an open thread
  const [sending, setSending] = useState(false);

  // Load all provider messages
  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const msgs = await getMessagesForProvider(providerId);
        if (!cancelled) setAllMessages(Array.isArray(msgs) ? msgs : []);
      } catch {
        if (!cancelled) setErr("Failed to load messages.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  // Build threads grouped by the patient participant
  const threads = useMemo(() => {
    const byPatient = new Map(); // patientId -> { patientId, messages: [], lastMessage: {...} }
    for (const m of allMessages) {
      const patientId = m.senderPatientId ?? m.recipientPatientId; // the patient party
      if (!patientId) continue;
      if (!byPatient.has(patientId)) {
        byPatient.set(patientId, { patientId, messages: [] });
      }
      byPatient.get(patientId).messages.push(m);
    }
    // compute lastMessage for preview
    for (const t of byPatient.values()) {
      t.messages.sort(
        (a, b) => new Date(a.sentAt || 0) - new Date(b.sentAt || 0),
      );
      t.lastMessage = t.messages[t.messages.length - 1];
    }
    // newest first in list
    return Array.from(byPatient.values()).sort(
      (a, b) =>
        new Date(b.lastMessage?.sentAt || 0) -
        new Date(a.lastMessage?.sentAt || 0),
    );
  }, [allMessages]);

  // Lookup patient names for thread list
  useEffect(() => {
    const missing = threads
      .map((t) => t.patientId)
      .filter((id) => id && !nameCache[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const p = await getPatient(id);
              const full =
                [p.firstName, p.lastName].filter(Boolean).join(" ") ||
                `Patient ${id}`;
              return [id, full];
            } catch {
              return [id, `Patient ${id}`];
            }
          }),
        );
        if (!cancelled) {
          setNameCache((prev) => {
            const next = { ...prev };
            for (const [id, name] of results) next[id] = name;
            return next;
          });
        }
      } catch {
        // ignore name failures; fall back to "Patient {id}"
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threads, nameCache]);

  // Derived: messages for the open thread
  const activeMessages = useMemo(() => {
    if (!activePatientId) return [];
    const t = threads.find((x) => x.patientId === activePatientId);
    return t ? t.messages : [];
  }, [threads, activePatientId]);

  // Actions
  const openThread = (patientId) => setActivePatientId(patientId);
  const backToThreads = () => {
    setActivePatientId(null);
    setComposer("");
  };

  const onSend = async (e) => {
    e.preventDefault();
    if (!composer.trim() || !activePatientId) return;
    setSending(true);
    setErr("");
    try {
      const created = await sendMessage({
        senderProviderId: providerId,
        recipientPatientId: activePatientId,
        content: composer.trim(),
      });
      // Merge new message into allMessages
      setAllMessages((prev) => [created, ...prev]);
      setComposer("");
    } catch {
      setErr("Failed to send.");
    } finally {
      setSending(false);
    }
  };

  const onDelete = async (messageId) => {
    try {
      await deleteMessage(messageId, { providerId });
      setAllMessages((prev) => prev.filter((m) => m.messageId !== messageId));
    } catch {
      alert("Failed to delete message.");
    }
  };

  // UI helpers
  const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "");
  const displayName = (pid) => nameCache[pid] || `Patient ${pid}`;

  // -------- Render --------
  return (
    <div className="w-full p-4 pl-0">
      {err && <div className="text-red-600">{err}</div>}
      {loading && <div>Loading…</div>}

      {!activePatientId ? (
        // Thread list
        <div className="space-y-3">
          {threads.length === 0 && !loading ? (
            <div className="text-sm text-gray-600">No messages yet.</div>
          ) : (
            threads.map((t) => (
              <button
                key={t.patientId}
                onClick={() => openThread(t.patientId)}
                className="w-full text-left p-3 border rounded bg-white hover:shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{displayName(t.patientId)}</div>
                  <div className="text-xs text-gray-500">
                    {fmtTs(t.lastMessage?.sentAt)}
                  </div>
                </div>
                <div className="text-sm text-gray-700 line-clamp-2 mt-1">
                  {t.lastMessage?.content || "—"}
                </div>
              </button>
            ))
          )}
        </div>
      ) : (
        // Thread detail
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold">
              {displayName(activePatientId)}
            </h4>
            <button
              onClick={backToThreads}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              ← Back
            </button>
          </div>

          <div className="space-y-3">
            {activeMessages.length === 0 ? (
              <div className="text-sm text-gray-600">
                No messages in this thread yet.
              </div>
            ) : (
              activeMessages.map((m) => {
                const mine = m.senderProviderId === providerId;
                return (
                  <div
                    key={m.messageId}
                    className="p-3 border rounded bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-gray-600">
                          {mine ? "You" : displayName(activePatientId)} •{" "}
                          {fmtTs(m.sentAt)}
                        </div>
                        <div className="mt-1">{m.content}</div>
                      </div>
                      <button
                        onClick={() => onDelete(m.messageId)}
                        className="text-red-600 text-sm"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Compose only inside an open thread */}
          <form onSubmit={onSend} className="mt-2">
            <textarea
              className="w-full border p-2 rounded"
              rows={2}
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder={`Message ${displayName(activePatientId)}…`}
              disabled={sending}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                disabled={sending || !composer.trim()}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
